import { useAsyncData } from '#imports'
import { type MaybeRefOrGetter, computed, ref, toValue } from 'vue'

import type { AlgoliaProduct } from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { algoliaHitToProduct } from '@backmarket/nuxt-layer-recommendation/helpers.ts'
import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import algoliasearch from 'algoliasearch'

import {
  LIVE_SHOPPING_CONTENT,
  LIVE_SHOPPING_GRADES,
  type LiveShoppingCategory,
  type LiveShoppingGrade,
} from '../constants'

export type LiveShoppingProduct = Product & { timestampSeconds: number }

// objectID format: `<productId>_<gradeId>_<offerType>` (see constants.ts).
const OFFER_TYPE = 0

function objectIdFor(productUuid: string, grade: LiveShoppingGrade) {
  return `${productUuid}_${grade}_${OFFER_TYPE}`
}

function gradeOf(objectID: string): LiveShoppingGrade | undefined {
  const grade = Number(objectID.split('_')[1])

  return LIVE_SHOPPING_GRADES.includes(grade as LiveShoppingGrade)
    ? (grade as LiveShoppingGrade)
    : undefined
}

// `immediate: false` + the caller invoking `fetchOnce()` on overlay open (not
// on mount) is what makes this genuinely lazy-on-open, not just deferred —
// and it avoids fetching products for every homepage visitor.
//
// `fetchOnce` (not the raw `execute`) is what a consumer should call: a
// useAsyncData created inside a component that only mounts client-side,
// late, behind ClientOnly + v-if reports `status: 'success'` immediately
// (confirmed live, not just per the POC handoff doc) even with
// `immediate: false` — so gating a call to `execute()` on `status.value`
// silently no-ops. `hasFetched` tracks readiness ourselves instead of
// trusting `status` for that.
export function useLiveShoppingProducts(
  category: MaybeRefOrGetter<LiveShoppingCategory>,
) {
  const algoliaSearchOptions = useAlgoliaSearchOptions('search')
  const $httpFetch = useHttpFetch()

  // The curated list for the page's category (iPad pages get the iPad
  // video's list, MacBook pages/Home the MacBook video's — see
  // useIsLiveShoppingEnabled.ts). Read via `toValue` inside the handler,
  // and passed to `watch`, so a category flip across SPA navigation
  // re-fetches without the widget remounting.
  const featuredProducts = computed(
    () => LIVE_SHOPPING_CONTENT[toValue(category)].products,
  )

  // Each curated UUID in every grade — grade selection happens at mapping
  // time (below), so a grade-level sellout degrades to another grade
  // instead of dropping the product. Only a fully out-of-stock UUID drops
  // out (needs re-curation — the Air M4 variant death of 2026-10-20).
  const objectIds = computed(() =>
    featuredProducts.value.flatMap((featured) =>
      LIVE_SHOPPING_GRADES.map((grade) =>
        objectIdFor(featured.productUuid, grade),
      ),
    ),
  )

  const { data, status, error, execute } = useAsyncData<AlgoliaProduct[]>(
    getAsyncDataKey('live-shopping-products'),
    async () => {
      const ids = objectIds.value
      if (ids.length === 0) return []

      let indexName: string | undefined
      try {
        const searchConfig = await $httpFetch(
          '/bm/search/v2/configuration/:scope',
          { pathParams: { scope: 'search-landing' } },
        )
        indexName = searchConfig?.indexes?.active?.name
      } catch {
        return []
      }

      if (!indexName) return []

      const algoliaClient = algoliasearch(
        'dummy_app_id',
        'dummy_key',
        algoliaSearchOptions,
      )

      const filters = ids.map((id) => `objectID:"${id}"`).join(' OR ')

      try {
        const result = await algoliaClient
          .initIndex(indexName)
          .search<AlgoliaProduct>('', {
            filters,
            hitsPerPage: ids.length,
            // The index configures Algolia `distinct` — one representative
            // record per variant (the cheapest in-stock grade) — which
            // would collapse this per-grade query back to a single grade
            // per product and make the preferred-grade selection below
            // unreachable. Ask for every record (discovered 2026-10-20:
            // health-check returned exactly one grade per UUID while
            // `distinct: false` enumeration showed several in stock).
            distinct: false,
          })

        return result.hits
      } catch {
        return []
      }
    },
    {
      immediate: false,
      default: () => [],
      // Auto re-fetch when the page's category changes mid-session (e.g.
      // SPA navigation from an iPad PDP to a MacBook PDP).
      watch: [objectIds],
    },
  )

  const hasFetched = ref(false)

  function fetchOnce() {
    if (hasFetched.value) return Promise.resolve()
    hasFetched.value = true

    return execute()
  }

  const products = computed<LiveShoppingProduct[]>(() => {
    // Group the fetched hits per curated product — several grades of the
    // same UUID can be in stock simultaneously.
    const hitsByProduct = new Map<string, AlgoliaProduct[]>()
    for (const hit of data.value ?? []) {
      if (gradeOf(hit.objectID) === undefined) continue

      const [productUuid] = hit.objectID.split('_')
      const existing = hitsByProduct.get(productUuid)

      if (existing) {
        existing.push(hit)
      } else {
        hitsByProduct.set(productUuid, [hit])
      }
    }

    // Chronological order (the order products appear in the video), not the
    // order they happen to be curated in `constants.ts` — the list is edited
    // piecemeal as content is resolved, so the two drift apart easily.
    return featuredProducts.value
      .flatMap((featured) => {
        const candidates = hitsByProduct.get(featured.productUuid) ?? []
        if (candidates.length === 0) return []

        // Janos's rule (2026-10-20): prefer the curated grade (the one
        // demonstrated in the video); fall back to the cheapest in-stock
        // grade. `price` arrives as a string or number depending on the hit.
        const hit =
          candidates.find(
            (candidate) =>
              gradeOf(candidate.objectID) === featured.preferredGrade,
          ) ??
          [...candidates].sort((a, b) => Number(a.price) - Number(b.price))[0]

        return [
          {
            // Grade link, not the master/parent one: the curated card shows
            // this specific hit (grade, storage, colour, price), so the PDP
            // must open on that same backbox instead of a model page whose
            // default configuration may not match what was displayed.
            ...algoliaHitToProduct(hit, { useGradeLink: true }),
            timestampSeconds: featured.timestampSeconds,
          },
        ]
      })
      .sort((a, b) => a.timestampSeconds - b.timestampSeconds)
  })

  return { products, status, error, fetchOnce }
}
